import React, { useRef } from "react"
import {
  Modal,
  Typography,
  Space,
  Button,
  message,
  Alert,
} from "@mimber/ui-web"
import { SendOutlined } from "@mimber/ui-web"
import {
  usePublishAnnouncement,
  useAnnouncementSchedule,
} from "@mimber/client/mimber-organization"
import { ScheduledActionStatus } from "@mimber/schema"
import type { PublishAnnouncementModalProps } from "./PublishAnnouncementModal.types"

const { Paragraph } = Typography

export const PublishAnnouncementModal: React.FunctionComponent<
  PublishAnnouncementModalProps
> = ({ announcement, onSuccess, ...modalProps }) => {
  const closeRef = useRef<(() => void) | null>(null)
  const externalOnCancel = (modalProps as { onCancel?: () => void }).onCancel

  const { data: scheduleData } = useAnnouncementSchedule(announcement.uuid)
  const schedule = scheduleData?.data
  const hasPendingSchedule = schedule?.status === ScheduledActionStatus.PENDING

  const mutation = usePublishAnnouncement({
    onSuccess: () => {
      message.success("Announcement published successfully")
      closeRef.current?.()
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to publish announcement: ${error.message}`)
    },
  })

  return (
    <Modal title="Publish Announcement" footer={null} {...modalProps}>
      {(close) => {
        closeRef.current = close
        return (
          <Space orientation="vertical" size="large" style={{ width: "100%" }}>
            {hasPendingSchedule ? (
              <Alert
                type="info"
                title="This announcement has a scheduled send"
                description={`A send is scheduled for ${new Date(schedule!.scheduledFor).toLocaleString()}. Choosing "Send Now" will send immediately — the announcement will also send again on the scheduled date.`}
                showIcon
              />
            ) : (
              <Alert
                type="info"
                title="Announcement will be sent immediately"
                description="Publishing will change this announcement's status to Published and immediately send it to all targeted recipients."
                showIcon
              />
            )}
            <Paragraph>
              Are you sure you want to publish this announcement?
            </Paragraph>
            <Space style={{ justifyContent: "flex-end", width: "100%" }}>
              <Button
                onClick={() => {
                  close()
                  externalOnCancel?.()
                }}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              {hasPendingSchedule ? (
                <>
                  <Button
                    disabled={mutation.isPending}
                    onClick={() =>
                      mutation.mutate({
                        uuid: announcement.uuid,
                        sendNow: false,
                      })
                    }
                  >
                    Publish & Wait for Schedule
                  </Button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    loading={mutation.isPending}
                    onClick={() =>
                      mutation.mutate({
                        uuid: announcement.uuid,
                        sendNow: true,
                      })
                    }
                  >
                    Publish & Send Now
                  </Button>
                </>
              ) : (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={mutation.isPending}
                  onClick={() =>
                    mutation.mutate({ uuid: announcement.uuid, sendNow: true })
                  }
                >
                  Publish Now
                </Button>
              )}
            </Space>
          </Space>
        )
      }}
    </Modal>
  )
}
